import { cloneDeep, mergeWith } from 'es-toolkit';
import {
	PropsWithChildren,
	createContext,
	isValidElement,
	memo,
	use,
	useCallback,
	useMemo,
	useState,
} from 'react';

import { defaultLocaleCode } from '../../../app.config';
import {
	Translations as BuyEasyTranslations,
	defaultTranslations as buyeasyDefaultTranslations,
} from '../../../studio/schemas/translations/buyeasy/schemas';
import {
	Translations,
	defaultTranslations,
} from '../../../studio/schemas/translations/general/schemas';

export type AllTranslations = {
	general: Translations;
	buyeasy: BuyEasyTranslations;
};

const createOrdinalRules = (locale: string) => {
	return new Intl.PluralRules(locale, { type: 'ordinal' });
};

export const withParams = (
	translation: string,
	params: Record<string, string | number>,
): string => {
	return Object.entries(params).reduce((acc, [key, value]) => {
		return acc.replaceAll(`{${key}}`, value.toString());
	}, translation);
};

export const withReactParams = (
	translation: string,
	params: Record<string, string | number | React.ReactNode>,
): Array<string | React.ReactNode> => {
	// Split the string at each {key} and insert the value (string, number, or React node)
	let result: Array<string | React.ReactNode> = [translation];
	for (const [key, value] of Object.entries(params)) {
		const next: Array<string | React.ReactNode> = [];
		result.forEach((part) => {
			if (typeof part !== 'string') {
				next.push(part);
				return;
			}
			const split = part.split(`{${key}}`);
			for (let i = 0; i < split.length; i++) {
				next.push(split[i]);
				if (i < split.length - 1) {
					// Convert primitives to string, keep React nodes as-is
					next.push(isValidElement(value) ? value : String(value ?? ''));
				}
			}
		});
		result = next;
	}
	// Remove empty strings for cleaner output
	return result.filter((x) => x !== '');
};

const createWithPlurals = (rules: Intl.PluralRules) => {
	return (translation: [string, string], params: { count: number }) => {
		const key = rules.select(params.count);
		const [one, other] = translation;
		const value = key === 'one' ? one : other;
		return value.replaceAll('{count}', params.count.toString());
	};
};

type TranslationsContextProps = {
	t: AllTranslations & {
		withParams: typeof withParams;
		withReactParams: typeof withReactParams;
		withPlurals: ReturnType<typeof createWithPlurals>;
	};
	setTranslations: (translations: AllTranslations) => void;
	setLocale: (locale: string) => void;
};

const createEmptyTranslations: () => AllTranslations = () => {
	const t: AllTranslations = {
		general: defaultTranslations,
		buyeasy: buyeasyDefaultTranslations,
	};

	return t;
};

const TranslationsContext = createContext<TranslationsContextProps>({
	t: {
		...createEmptyTranslations(),
		withParams,
		withReactParams,
		withPlurals: createWithPlurals(createOrdinalRules(defaultLocaleCode)),
	},
	setTranslations: () => {},
	setLocale: () => {},
});

export const useTranslations = () => use(TranslationsContext);

export const TranslationsProvider = memo(({ children }: PropsWithChildren) => {
	const [ordinalRules, setOrdinalRules] = useState(createOrdinalRules(defaultLocaleCode));

	const [translations, setTranslations] = useState<AllTranslations>(createEmptyTranslations());

	const setLocale = useCallback((locale: string) => {
		setOrdinalRules(createOrdinalRules(locale));
	}, []);

	const updateTranslations = useCallback((translations: AllTranslations) => {
		setTranslations(() =>
			mergeWith(
				cloneDeep(createEmptyTranslations()),
				translations,
				(defaultValue, translationValue) => {
					if (translationValue === '' || Array.isArray(translationValue)) return defaultValue;
				},
			),
		);
	}, []);

	const value = useMemo<TranslationsContextProps>(
		() => ({
			t: {
				...translations,
				withParams,
				withReactParams,
				withPlurals: createWithPlurals(ordinalRules),
			},
			setLocale,
			setTranslations: updateTranslations,
		}),
		[translations, ordinalRules, setLocale, updateTranslations],
	);

	return <TranslationsContext value={value}>{children}</TranslationsContext>;
});

TranslationsProvider.displayName = 'TranslationsProvider';
